import React, { useEffect, useMemo } from "react";
import { Stack, FormControlLabel, Checkbox, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import LinkIcon from "@mui/icons-material/Link";

import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { linkStudentToParent } from "../redux/actions/parentActions";
import { getStudents } from "../redux/actions/studentActions";

import { RelationshipType } from "../shared/enums/RelationshipType";
import { StudentParentGuardianLinkDto } from "../shared/dtos/parentGuardian/StudentParentGuardianLinkDto";

const linkSchema = Yup.object().shape({
  studentId: Yup.string().required("Student selection is required"),
  relationshipType: Yup.mixed<RelationshipType>()
    .oneOf(Object.values(RelationshipType))
    .required("Relationship is required"),
  isPrimaryContact: Yup.boolean().required(),
  canPickup: Yup.boolean().required(),
});

const relationshipOptions = Object.values(RelationshipType).map((r) => ({
  value: r,
  label: r.charAt(0) + r.slice(1).toLowerCase().replace(/_/g, " "),
}));

interface LinkStudentToParentFormProps {
  parentId: string;
  loading?: boolean;
}

const LinkStudentToParentForm: React.FC<LinkStudentToParentFormProps> = ({
  parentId,
  loading = false,
}) => {
  const dispatch = useAppDispatch();
  const { items: students } = useAppSelector((state) => state.student);

  useEffect(() => {
    if (!students || students.length === 0) {
      dispatch(getStudents());
    }
  }, [dispatch, students]);

  const studentOptions = useMemo(
    () =>
      students?.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName} (${s.studentId || "No ID"})`,
      })) || [],
    [students],
  );

  const initialValues: StudentParentGuardianLinkDto = {
    parentId,
    studentId: "",
    relationshipType: RelationshipType.MOTHER,
    isPrimaryContact: false,
    canPickup: true,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={linkSchema}
      onSubmit={(values) => {
        dispatch(linkStudentToParent(values));
      }}
    >
      {({
        isValid,
        dirty,
        values,
        handleChange,
      }: FormikProps<StudentParentGuardianLinkDto>) => (
        <Form>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <FormSelectField
                name="studentId"
                label="Select Student"
                options={studentOptions}
              />
              <FormSelectField
                name="relationshipType"
                label="Relationship Type"
                options={relationshipOptions}
              />
            </Box>

            <Stack
              direction="row"
              spacing={3}
              sx={{ justifyContent: "flex-start" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isPrimaryContact}
                    onChange={handleChange}
                    name="isPrimaryContact"
                  />
                }
                label="Primary Contact"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.canPickup}
                    onChange={handleChange}
                    name="canPickup"
                  />
                }
                label="Authorized to Pickup"
              />
            </Stack>

            <SubmitButton
              title="Link Student"
              loadingText="Linking..."
              loading={loading}
              disabled={!dirty || !isValid || loading}
              startIcon={<LinkIcon />}
              sx={{ mt: 1 }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default LinkStudentToParentForm;
